import React from "react";
import { Loader2, Pause, Play, X, AlertCircle } from "lucide-react";
import { useSearchParams } from "@remix-run/react";

import { BillingBlockedAlert } from "~/components/billing/billing-blocked-alert";
import { Button } from "~/components/ui/button";
import { useStreamingMeditation } from "../hooks/use-streaming-meditation";
import type {
    MeditationOpening,
    MeditationPauseMultiplier,
    MeditationType,
} from "~/features/meditations/types";
import { FullscreenStreamingPlayer } from "./fullscreen-streaming-player";
import type { CaptureField, CaptureKind } from "./meditation-capture-panel";
import { getConnectionIssueLine } from "./meditation-buffering-indicator";

// AIDEV-NOTE: Streaming meditation player with intro + main audio support
// Uses SSE to stream audio generation in real-time
// AIDEV-NOTE: Now supports fullscreen mode with breathing animation during loading
// AIDEV-NOTE: EVO-1954 — the "enable-meditation-non-safari-hls" trial flag is
// retired: hls.js is now the DEFAULT for non-native browsers (unified HLS
// transport for all meditation types; durability > time-to-first-audio).

function browserSupportsNativeHlsPlayback(): boolean {
    if (typeof document === "undefined") {
        return false;
    }

    // AIDEV-NOTE: Chrome on macOS reports canPlayType("application/vnd.apple.mpegurl")
    // as "maybe" but cannot actually demux CMAF/fMP4 HLS segments, resulting in
    // DEMUXER_ERROR_COULD_NOT_PARSE. Only trust native HLS on Safari/WebKit.
    const ua = navigator.userAgent;
    const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS/.test(ua);
    if (!isSafari) {
        return false;
    }

    const audio = document.createElement("audio");
    try {
        return audio.canPlayType("application/vnd.apple.mpegurl") !== "";
    } catch {
        return false;
    }
}

type Props = {
    type: MeditationType;
    voice: string;
    pauseMultiplier?: MeditationPauseMultiplier;
    opening?: MeditationOpening;
    sessionId?: string;
    partInteractionId?: string;
    sessionToken?: string;
    title?: string;
    // AIDEV-NOTE: Overrides the title persisted by the in-player capture rail (pcc_card entry
    // title) without changing the visible `title` header. Defaults to `title` when omitted.
    captureTitle?: string;
    partName?: string; // AIDEV-NOTE: Part name for part meditations (shown alongside meditation type)
    // AIDEV-NOTE: Selected part's generated image (signed URL); shown as the player icon when present.
    partImageUrl?: string;
    // AIDEV-NOTE: Resource-contextual meditation label; overrides the generic type label subtitle.
    meditationTypeLabel?: string;
    sticky?: boolean;
    autoStart?: boolean;
    fullscreen?: boolean; // AIDEV-NOTE: Use fullscreen mode (auto-enabled on mobile)
    onClose?: () => void;
    // AIDEV-NOTE: Force fallback mode for testing (e.g., ?forceFallback=1 URL param)
    forceFallback?: boolean;
    // AIDEV-NOTE: Explicit override for the experimental non-Safari HLS.js path.
    // When omitted, the player falls back to ?forceHlsJs=1 or the segment-backed flag.
    forceHlsJs?: boolean;
    // AIDEV-NOTE: Pass meditationId to stream a pre-cached meditation by ID.
    // This enables fullscreen playback for existing meditations without re-generation.
    meditationId?: string;
    // AIDEV-NOTE: Override the SSE stream URL (e.g. /api/exercise/stream for resource exercises)
    streamUrl?: string;
    // AIDEV-NOTE: EVO-1954 — exercise generation params; when set, exercises
    // use the same durable HLS bootstrap as every other meditation type.
    experienceId?: string;
    selectedPartIds?: string[];
    // AIDEV-NOTE: EVO-2476 — approximate total (seconds) shown until the real one is known.
    estimatedTotalSeconds?: number | null;
    // AIDEV-NOTE: Strip the default card styling (border, shadow, bg) for embedded contexts
    unstyled?: boolean;
    // AIDEV-NOTE: In-meditation capture rail config, forwarded to the fullscreen player. Defaults
    // to a "Reflect" journal (meditation_journal); resource exercises pass pcc_card / note / name.
    captureKind?: CaptureKind;
    captureFields?: CaptureField[];
    capturePartId?: string;
    // AIDEV-NOTE: Selected part's inferred type — forwarded so the PCC capture rail can render
    // type-conditional fields (never auto-saved).
    capturePartType?: string | null;
    // AIDEV-NOTE: Fired after an in-player rename so the host can update its part list.
    onRename?: (partId: string, newName: string) => void;
    // AIDEV-NOTE: See FullscreenStreamingPlayer — the launcher already tracked the start.
    startedEventTracked?: boolean;
};

export const StreamingMeditationPlayer: React.FC<Props> = ({
    type,
    voice,
    pauseMultiplier = 1,
    opening = "full",
    sessionId,
    partInteractionId,
    sessionToken,
    title = "Meditation",
    captureTitle,
    partName,
    partImageUrl,
    meditationTypeLabel,
    sticky = false,
    autoStart = false,
    fullscreen: fullscreenProp,
    onClose,
    forceFallback,
    forceHlsJs,
    meditationId,
    streamUrl,
    experienceId,
    selectedPartIds,
    estimatedTotalSeconds,
    unstyled,
    captureKind,
    captureFields,
    capturePartId,
    capturePartType,
    onRename,
    startedEventTracked,
}) => {
    const [searchParams] = useSearchParams();
    const [hlsFullyBufferedDuration, setHlsFullyBufferedDuration] =
        React.useState(false);
    const [hasObservedHlsPlayback, setHasObservedHlsPlayback] =
        React.useState(false);
    // AIDEV-NOTE: Use fullscreen mode if explicitly set, or auto-enable when autoStart is true
    const useFullscreen = fullscreenProp ?? autoStart;
    const forceHlsJsQueryParam = searchParams.get("forceHlsJs") === "1";
    const browserReportsNativeHls = browserSupportsNativeHlsPlayback();
    const enableHlsJsTrial =
        // AIDEV-NOTE: EVO-1954 — HLS is the unified transport for ALL
        // meditation types (durability > time-to-first-audio). Exercises pass
        // experienceId and bootstrap HLS too; only bare-streamUrl callers stay
        // SSE-only.
        streamUrl && !experienceId
            ? false
            : // AIDEV-NOTE: Explicit forceHlsJs overrides are a debug/test escape
              // hatch and must bypass native-HLS detection. Safari/iOS WebKit
              // keep their native HLS path; every other browser now defaults to
              // hls.js (the former NON_SAFARI_HLS trial flag is the default).
              forceHlsJs === true || forceHlsJsQueryParam
              ? true
              : forceHlsJs === false
                ? false
                : !browserReportsNativeHls;

    React.useEffect(() => {
        // AIDEV-NOTE: Local smoke debugging depends on seeing whether the app
        // chose native HLS/SSE or the non-Safari hls.js path after query/env overrides.
        console.info("[StreamingMeditationPlayer] playback path flags", {
            forceHlsJsQueryParam,
            forceHlsJsProp: forceHlsJs ?? null,
            browserReportsNativeHls,
            enableHlsJsTrial,
            forceFallback: forceFallback ?? false,
            fullscreen: useFullscreen,
            autoStart,
            type,
            pauseMultiplier,
        });
    }, [
        autoStart,
        browserReportsNativeHls,
        enableHlsJsTrial,
        forceFallback,
        forceHlsJs,
        forceHlsJsQueryParam,
        type,
        pauseMultiplier,
        useFullscreen,
    ]);

    // AIDEV-NOTE: Render fullscreen player if enabled
    if (useFullscreen) {
        return (
            <FullscreenStreamingPlayer
                type={type}
                voice={voice}
                pauseMultiplier={pauseMultiplier}
                opening={opening}
                sessionId={sessionId}
                partInteractionId={partInteractionId}
                sessionToken={sessionToken}
                title={title}
                captureTitle={captureTitle}
                partName={partName}
                partImageUrl={partImageUrl}
                meditationTypeLabel={meditationTypeLabel}
                onClose={onClose}
                forceFallback={forceFallback}
                forceHlsJs={enableHlsJsTrial}
                meditationId={meditationId}
                streamUrl={streamUrl}
                experienceId={experienceId}
                selectedPartIds={selectedPartIds}
                estimatedTotalSeconds={estimatedTotalSeconds}
                captureKind={captureKind}
                captureFields={captureFields}
                capturePartId={capturePartId}
                capturePartType={capturePartType}
                onRename={onRename}
                startedEventTracked={startedEventTracked}
            />
        );
    }

    const player = useStreamingMeditation({
        type,
        voice,
        pauseMultiplier,
        opening,
        sessionId,
        partInteractionId,
        sessionToken,
        forceFallback,
        forceHlsJs: enableHlsJsTrial,
        meditationId,
        streamUrl,
        experienceId,
        selectedPartIds,
    });

    // AIDEV-NOTE: Track if autoStart has already triggered to prevent multiple calls
    // The player.start callback changes when phase changes, which would re-trigger the effect
    // DO NOT reset this ref in effects - it should persist to prevent double-invocation in strict mode
    const hasAutoStartedRef = React.useRef(false);

    // AIDEV-NOTE: Auto-start playback when autoStart prop is true (runs once)
    React.useEffect(() => {
        if (
            autoStart &&
            !hasAutoStartedRef.current &&
            player.phase === "idle" &&
            !player.isLoading
        ) {
            hasAutoStartedRef.current = true;
            // AIDEV-NOTE: Defer start to avoid React Strict Mode effect cleanup
            // interrupting AudioController initialization on the first pass.
            setTimeout(() => {
                player.start();
            }, 0);
        }
    }, [autoStart, player.phase, player.isLoading, player.start]);

    const formatTime = (sec: number) => {
        if (!isFinite(sec) || sec < 0) return "00:00";
        const m = Math.floor(sec / 60)
            .toString()
            .padStart(2, "0");
        const s = Math.floor(sec % 60)
            .toString()
            .padStart(2, "0");
        return `${m}:${s}`;
    };

    // AIDEV-NOTE: Monotonic-latched total duration. The underlying
    // player.totalDuration can flicker because (a) multiple SSE "complete"
    // events fire with growing durations as incremental batch generation
    // progresses, and (b) hlsFullyBufferedDuration toggles mid-stream as
    // the buffer drains past threshold. Latching to the max-ever value
    // stops the UI from flickering between the current value and "...".
    const latchedTotalDurationRef = React.useRef(0);
    if (
        Number.isFinite(player.totalDuration) &&
        player.totalDuration > latchedTotalDurationRef.current
    ) {
        latchedTotalDurationRef.current = player.totalDuration;
    }
    const latchedTotalDuration = latchedTotalDurationRef.current;

    // Calculate progress percentage
    const progress =
        latchedTotalDuration > 0
            ? Math.min(100, (player.currentTime / latchedTotalDuration) * 100)
            : 0;
    const shouldShowTotalDuration =
        !player.awaitingAudiblePlaybackStart &&
        (!hasObservedHlsPlayback || hlsFullyBufferedDuration);

    React.useEffect(() => {
        const handleHlsBufferingStatus = (event: Event) => {
            const detail = (
                event as CustomEvent<{
                    isFullyBuffered?: boolean;
                }>
            ).detail;
            setHasObservedHlsPlayback(true);
            setHlsFullyBufferedDuration(Boolean(detail?.isFullyBuffered));
        };

        window.addEventListener(
            "meditation:hls-buffering-status",
            handleHlsBufferingStatus,
        );
        return () => {
            window.removeEventListener(
                "meditation:hls-buffering-status",
                handleHlsBufferingStatus,
            );
        };
    }, []);

    // AIDEV-NOTE: Show start button only when truly idle — not during the gap between
    // GENERATION_COMPLETE (isGenerating=false) and MAIN_PLAYBACK_STARTED (phase→main).
    // mainDuration > 0 means generation finished; isGenerating means chunks are streaming.
    // AIDEV-NOTE: EVO-1815 — terminal errors now reset phase to idle, so the
    // !player.error guard keeps the error/Retry UI from being masked by this branch.
    if (
        player.phase === "idle" &&
        !player.error &&
        !player.isLoading &&
        !player.isGenerating &&
        !player.mainDuration &&
        !autoStart
    ) {
        return (
            <div
                className={
                    unstyled ? "" : "rounded-md border bg-white p-4 shadow-lg"
                }
            >
                <div className="mb-2 text-center text-sm font-medium text-gray-700">
                    {title}
                </div>
                <div className="flex justify-center">
                    <Button
                        onClick={player.start}
                        variant="default"
                        className="gap-2"
                    >
                        <Play className="size-4" />
                        Start Meditation
                    </Button>
                </div>
            </div>
        );
    }

    // Error state
    if (player.error) {
        const isBillingReadOnly =
            player.billingBlocked?.thresholdState === "blocked";
        return (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 shadow-lg">
                {player.billingBlocked ? (
                    <BillingBlockedAlert billing={player.billingBlocked} />
                ) : (
                    <div className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="size-5" />
                        <span className="text-sm font-medium">
                            {player.error}
                        </span>
                    </div>
                )}
                <div className="mt-3 flex justify-center gap-2">
                    {!isBillingReadOnly && (
                        <Button
                            onClick={player.start}
                            variant="outline"
                            size="sm"
                        >
                            Retry
                        </Button>
                    )}
                    <Button
                        onClick={() => {
                            player.stop();
                            onClose?.();
                        }}
                        variant="ghost"
                        size="sm"
                    >
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    // AIDEV-NOTE: Buffering connection line (Margie incident). The play button
    // swaps to a spinner while buffering; this only adds the offline explanation.
    // slowNetworkDetected is intentionally excluded — the amber banner at the
    // top of the inline player already covers it.
    // AIDEV-NOTE: EVO-1813 — isStalled is debounced ~2s in StallDetector; it only
    // fires on real stalls, never during intentional (encoded) meditation silence.
    const bufferingConnectionLine = player.isStalled
        ? getConnectionIssueLine({
              isOffline: player.isOffline,
              slowNetworkDetected: false,
          })
        : null;

    const content = (
        <div
            className={
                unstyled ? "" : "rounded-md border bg-white p-4 shadow-lg"
            }
        >
            {/* AIDEV-NOTE: Slow-network warning. Surfaces when the player has
                observed 2+ stall events in the first 10s of playback, which
                is a reliable signal that the user's connection can't keep up
                with the HLS segment cadence. See Gina 2026-04-14 (11 Mbps
                during a Zoom call). */}
            {player.slowNetworkDetected && (
                <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                    <AlertCircle className="size-4 shrink-0" />
                    <span>Slow or unstable connection detected</span>
                </div>
            )}
            {/* Title and status */}
            <div className="mb-2 flex items-center justify-center gap-2">
                <span className="truncate text-sm font-medium text-gray-700">
                    {title}
                </span>
                {player.statusLabel && (
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                        {(player.isLoading || player.isGenerating) && (
                            <Loader2 className="size-3 animate-spin" />
                        )}
                        {player.statusLabel}
                    </span>
                )}
                {/* AIDEV-NOTE: Debug badge showing cache status, non-production only */}
                {player.source &&
                    typeof window !== "undefined" &&
                    window.env?.ENV_NAME !== "production" && (
                        <span className="text-muted-foreground/60 text-[10px] font-mono">
                            ({player.source === "cached" ? "cached" : "new"})
                        </span>
                    )}
            </div>

            <div className="flex items-center gap-4">
                {/* Play/Pause button */}
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (player.isPlaying) {
                            player.pause();
                        } else {
                            player.play();
                        }
                    }}
                    disabled={
                        player.isLoading ||
                        (player.phase === "gap" && !player.mainDuration)
                    }
                    className="!h-10 !w-10 [&_svg]:!size-6"
                    aria-label={player.isPlaying ? "Pause" : "Play"}
                >
                    {player.isLoading || player.isStalled ? (
                        <Loader2 className="animate-spin" />
                    ) : player.isPlaying ? (
                        <Pause />
                    ) : (
                        <Play />
                    )}
                </Button>

                {/* Progress bar */}
                <div className="flex w-full items-center gap-3">
                    <span className="text-muted-foreground w-10 text-right text-xs tabular-nums">
                        {formatTime(player.currentTime)}
                    </span>
                    <div className="flex-1" style={{ marginTop: "-0.25rem" }}>
                        <div className="relative h-2 w-full rounded-lg bg-gray-200">
                            {/* Progress fill */}
                            <div
                                className="absolute left-0 top-0 h-full rounded-lg bg-brand-forest-700 transition-all"
                                style={{ width: `${progress}%` }}
                            />

                            {/* Phase indicators */}
                            {player.introDuration > 0 &&
                                latchedTotalDuration > 0 && (
                                    <>
                                        {/* Intro/main boundary marker */}
                                        <div
                                            className="absolute top-0 h-full w-0.5 bg-gray-400"
                                            style={{
                                                left: `${
                                                    (player.introDuration /
                                                        latchedTotalDuration) *
                                                    100
                                                }%`,
                                            }}
                                        />
                                    </>
                                )}

                            {/* Seek slider */}
                            <input
                                type="range"
                                min={0}
                                max={latchedTotalDuration || 0}
                                value={player.currentTime}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    player.seek(value);
                                }}
                                disabled={
                                    player.isLoading ||
                                    (!player.mainDuration &&
                                        player.phase !== "intro")
                                }
                                className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0"
                            />
                        </div>
                    </div>
                    <span className="text-muted-foreground w-10 text-xs tabular-nums">
                        {latchedTotalDuration > 0
                            ? formatTime(latchedTotalDuration)
                            : player.isGenerating || hasObservedHlsPlayback
                              ? "..."
                              : formatTime(player.introDuration)}
                    </span>
                </div>

                {/* Close button */}
                <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Close"
                    onClick={() => {
                        player.stop();
                        onClose?.();
                    }}
                >
                    <X className="size-4" />
                </Button>
            </div>

            {bufferingConnectionLine && (
                <p className="mt-2 text-center text-xs text-muted-foreground">
                    {bufferingConnectionLine}
                </p>
            )}
        </div>
    );

    if (!sticky) return content;

    // Sticky variant at bottom of screen
    return (
        <div className="fixed inset-x-0 bottom-20 z-50 p-4 md:bottom-0">
            <div className="mx-auto max-w-5xl md:pl-8">{content}</div>
        </div>
    );
};
